import { Globe, Server } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@shared/components/ui/cn';

import { formatPolicyDate, formatPolicyTime, policyValueKey } from '../labels';
import { bidiIsolate } from '../names';
import type { AiDataPolicy } from '../types';

export interface PolicySummaryCardProps {
  policy: AiDataPolicy;
  showMeta?: boolean;
  className?: string;
}

export function policyMeta(
  t: (key: string, options?: Record<string, unknown>) => string,
  policy: AiDataPolicy,
  rtl = false,
): string | null {
  if (policy.value === 'not_chosen') return t('summary.metaDefault');
  if (!policy.setAt) return null;
  const when = { date: formatPolicyDate(policy.setAt), time: formatPolicyTime(policy.setAt) };
  return policy.setBy?.name
    ? t('summary.metaSetBy', { name: bidiIsolate(policy.setBy.name, rtl), ...when })
    : t('summary.metaSetAt', when);
}

export function PolicySummaryCard({ policy, showMeta = true, className }: PolicySummaryCardProps) {
  const { t, i18n } = useTranslation('ai-data-policy');
  const Glyph = policy.value === 'commercial_allowed' ? Globe : Server;
  const bodyKey =
    policy.value === 'not_chosen'
      ? 'summary.notChosenBody'
      : policy.value === 'investbanq_only'
        ? 'summary.investbanqOnlyBody'
        : 'summary.commercialAllowedBody';
  const meta = showMeta ? policyMeta(t, policy, i18n.dir() === 'rtl') : null;

  return (
    <div className={cn('flex items-start gap-3 rounded-lg border bg-card p-3', className)}>
      <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-muted">
        <Glyph className="size-4 text-foreground" aria-hidden />
      </span>
      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
        <p className="text-sm font-medium text-foreground">{t(policyValueKey(policy.value))}</p>
        <p className="text-sm text-muted-foreground">{t(bodyKey)}</p>
        {meta && <p className="text-xs text-muted-foreground">{meta}</p>}
      </div>
    </div>
  );
}
